import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { Household } from './Household';
import type { License } from './License';

const API = 'https://api.lunaloo.net';

/**
 * Push notifications, two ways behind one switch:
 * - Web: VAPID web push through the service worker (Android Chrome; iPhone once the site is on
 *   the home screen).
 * - Store apps (Capacitor): the native token (FCM on Android, APNs on iOS) registered with the
 *   same API endpoint as `{ native: 'fcm' | 'apns', token }`; the server picks the transport.
 */
export class Push {
  constructor(
    private hh: Household,
    private license: License,
  ) {}

  get native(): boolean {
    return Capacitor.isNativePlatform();
  }

  get supported(): boolean {
    if (this.native) return true;
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && window.isSecureContext;
  }

  get enabled(): boolean {
    if (!this.supported || localStorage.getItem('lunaloo.push') !== '1') return false;
    return this.native || Notification.permission === 'granted';
  }

  private async reg(): Promise<ServiceWorkerRegistration> {
    return navigator.serviceWorker.register('/sw.js');
  }

  /** must be called from a user gesture */
  async enable(): Promise<string> {
    if (!this.supported) return 'not available in this browser';
    const code = await this.hh.ensureCode();
    if (!code) return 'needs the internet';
    return this.native ? this.enableNative(code) : this.enableWeb(code);
  }

  private async register(code: string, subscription: unknown): Promise<string> {
    const r = await fetch(`${API}/v1/households/${code}/push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device: this.license.device, subscription }),
    });
    if (!r.ok) return 'server said no';
    localStorage.setItem('lunaloo.push', '1');
    void fetch(`${API}/v1/households/${code}/push/${this.license.device}/test`, { method: 'POST' });
    return 'on';
  }

  private async enableWeb(code: string): Promise<string> {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return 'notifications blocked';
    try {
      const reg = await this.reg();
      const { key } = (await (await fetch(`${API}/v1/push/key`)).json()) as { key: string };
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
      return this.register(code, sub.toJSON());
    } catch (e) {
      return `couldn't subscribe: ${(e as Error).message}`;
    }
  }

  private async enableNative(code: string): Promise<string> {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return 'notifications blocked';
    const token = await new Promise<string>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('no token from the phone')), 15_000);
      void PushNotifications.addListener('registration', (t) => {
        window.clearTimeout(timer);
        resolve(t.value);
      });
      void PushNotifications.addListener('registrationError', (e) => {
        window.clearTimeout(timer);
        reject(new Error(e.error));
      });
      void PushNotifications.register();
    }).catch((e: Error) => e);
    if (token instanceof Error) return `couldn't register: ${token.message}`;
    // a notification tapped while the app is closed just brings the app up; nothing to route yet
    void PushNotifications.addListener('pushNotificationActionPerformed', () => window.focus());
    return this.register(code, { native: Capacitor.getPlatform() === 'ios' ? 'apns' : 'fcm', token });
  }

  async disable(): Promise<void> {
    localStorage.removeItem('lunaloo.push');
    try {
      if (this.native) {
        await PushNotifications.unregister();
      } else {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        const sub = await reg?.pushManager.getSubscription();
        await sub?.unsubscribe();
      }
      if (this.hh.code) await fetch(`${API}/v1/households/${this.hh.code}/push/${this.license.device}`, { method: 'DELETE' });
    } catch {
      /* ignore */
    }
  }
}

function urlBase64ToUint8Array(s: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
